import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { usersService } from '@/services/users';
import { hospitalsService } from '@/services/hospitals';
import { getApiErrorMessage } from '@/lib/api';
import { PencilSimple, Plus, Shield, Trash } from '@phosphor-icons/react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FEATURES } from '@/contexts/FeatureFlagContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Module labels only; which roles exist comes from GET /users/roles (DB). */
const MODULE_OPTIONS = [
  { id: FEATURES.DASHBOARD, label: 'Dashboard' },
  { id: FEATURES.PATIENTS, label: 'Patients' },
  { id: FEATURES.APPOINTMENTS, label: 'Appointments' },
  { id: FEATURES.IPD, label: 'IPD / Ward' },
  { id: FEATURES.PHARMACY, label: 'Pharmacy' },
  { id: FEATURES.LAB, label: 'Laboratory' },
  { id: FEATURES.BILLING, label: 'Billing' },
  { id: FEATURES.INVENTORY, label: 'Inventory' },
  { id: FEATURES.HR, label: 'HR & Staff' },
  { id: FEATURES.DOCUMENTS, label: 'Documents' },
  { id: FEATURES.AUDIT, label: 'Audit log' },
  { id: FEATURES.USERS, label: 'User management' },
  { id: FEATURES.MY_CALENDAR, label: 'My calendar' },
  { id: FEATURES.ROLE_MANAGEMENT, label: 'Role management' },
];

export default function RoleManagementPage() {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roleModuleMap, setRoleModuleMap] = useState({});
  const [savingRoleModules, setSavingRoleModules] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newRole, setNewRole] = useState({ roleKey: '', name: '', description: '' });
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ roleKey: '', name: '', description: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const loadRoles = useCallback(async () => {
    const { data } = await usersService.getRoles();
    setRoles(data);
  }, []);

  const loadRoleModules = useCallback(async () => {
    const { data } = await hospitalsService.getRoleModules();
    setRoleModuleMap(data.roleModuleAccess || {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadRoles(), loadRoleModules()]);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [loadRoles, loadRoleModules]);

  const toggleModule = (roleId, moduleId) => {
    setRoleModuleMap((prev) => {
      const list = prev[roleId] ?? [];
      const next = new Set(list);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return { ...prev, [roleId]: [...next] };
    });
  };

  const saveRoleModules = async () => {
    try {
      setSavingRoleModules(true);
      await hospitalsService.updateRoleModules(roleModuleMap);
      toast.success('Module access saved');
      await loadRoleModules();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSavingRoleModules(false);
    }
  };

  const createRole = async () => {
    const roleKey = newRole.roleKey.trim().toLowerCase();
    const name = newRole.name.trim();
    if (!roleKey || !name) {
      toast.error('Role key and display name are required');
      return;
    }
    try {
      setCreating(true);
      await hospitalsService.createCustomRole({
        roleKey,
        name,
        description: newRole.description.trim() || undefined,
      });
      toast.success('Role created');
      setAddOpen(false);
      setNewRole({ roleKey: '', name: '', description: '' });
      await Promise.all([loadRoles(), loadRoleModules()]);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (role) => {
    setEditForm({
      roleKey: role.id,
      name: role.name,
      description: role.description || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      setSavingEdit(true);
      await hospitalsService.updateCustomRole(editForm.roleKey, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
      });
      toast.success('Role updated');
      setEditOpen(false);
      await loadRoles();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const removeCustomRole = async (roleKey) => {
    if (
      !window.confirm(
        `Delete role "${roleKey}"? Users and staff must not use it anymore.`,
      )
    ) {
      return;
    }
    try {
      await hospitalsService.deleteCustomRole(roleKey);
      toast.success('Role deleted');
      await Promise.all([loadRoles(), loadRoleModules()]);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="role-management-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Role Management</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground max-w-2xl">
            Add hospital-specific roles, then choose which modules each role can access. Built-in
            roles and custom roles can be assigned when creating users or HR staff. Hospital
            administrators always have full access.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add role
          </Button>
          <Button type="button" onClick={saveRoleModules} disabled={savingRoleModules}>
            {savingRoleModules ? 'Saving…' : 'Save module access'}
          </Button>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add custom role</DialogTitle>
            <DialogDescription>
              Use a short unique key (lowercase, e.g. therapist). It cannot match a system role.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-role-key">Role key</Label>
              <Input
                id="new-role-key"
                placeholder="e.g. physiotherapist"
                value={newRole.roleKey}
                onChange={(e) =>
                  setNewRole((r) => ({ ...r, roleKey: e.target.value.toLowerCase() }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role-name">Display name</Label>
              <Input
                id="new-role-name"
                placeholder="e.g. Physiotherapist"
                value={newRole.name}
                onChange={(e) => setNewRole((r) => ({ ...r, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role-desc">Description (optional)</Label>
              <Input
                id="new-role-desc"
                placeholder="Short description"
                value={newRole.description}
                onChange={(e) => setNewRole((r) => ({ ...r, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createRole} disabled={creating}>
              {creating ? 'Creating…' : 'Create role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit role</DialogTitle>
            <DialogDescription>
              Role key <code className="text-xs bg-muted px-1 rounded">{editForm.roleKey}</code>{' '}
              cannot be changed. Display name and description are stored in the database.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-role-name">Display name</Label>
              <Input
                id="edit-role-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role-desc">Description</Label>
              <Input
                id="edit-role-desc"
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={savingEdit}>
              {savingEdit ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 md:grid-cols-2">
        {roles.map((role) => {
          if (role.id === 'hospital_admin') {
            return (
              <Card key={role.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" weight="duotone" />
                    {role.name}
                  </CardTitle>
                  <CardDescription>{role.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Hospital administrators always have access to every module, including this page.
                    Their permissions are not restricted here.
                  </p>
                </CardContent>
              </Card>
            );
          }

          const allowed = roleModuleMap[role.id] ?? [];
          return (
            <Card key={role.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="flex flex-wrap items-center gap-2">
                      <Shield className="h-5 w-5 text-primary shrink-0" weight="duotone" />
                      <span>{role.name}</span>
                      {role.isCustom ? (
                        <Badge variant="secondary" className="text-xs font-normal">
                          Custom
                        </Badge>
                      ) : role.isBuiltIn ? (
                        <Badge variant="outline" className="text-xs font-normal">
                          Built-in
                        </Badge>
                      ) : null}
                    </CardTitle>
                    <CardDescription className="mt-1">{role.description}</CardDescription>
                    <p className="text-xs text-muted-foreground mt-1 font-mono">Key: {role.id}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(role)}
                      aria-label="Edit role"
                    >
                      <PencilSimple className="h-4 w-4" />
                    </Button>
                    {role.isCustom ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeCustomRole(role.id)}
                        aria-label="Delete role"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm font-medium">Visible sections</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {MODULE_OPTIONS.map((mod) => (
                    <label
                      key={mod.id}
                      className="flex items-center gap-2 rounded-md border border-border/60 px-2 py-2 text-sm cursor-pointer hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={allowed.includes(mod.id)}
                        onCheckedChange={() => toggleModule(role.id, mod.id)}
                      />
                      <span>{mod.label}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
